import type {
  HokkaidoAirportReport,
  HokkaidoAirportReportLocalStorage,
  HokkaidoAirportState,
  HokkaidoAirportVehicleSelection,
} from '@/models/externalReports/hokkaidoAirport/spHokkaidoAirportReport';
import {
  timeIntegerToSeconds,
  unpackTimeInteger,
} from '@/lib/dateTimeHelper';
import { DEPARTURE_STORAGE_EXPIRE_HOURS } from '@/consts/externalReports/hokkaidoAirport';
import type {
  HokkaidoAirportLabelSp,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import type {
  HokkaidoAirportScreenName,
  HokkaidoAirportScreenSourceReport,
} from '@/components/Sp/ExternalReports/HokkaidoAirport/types';

export const initHokkaidoAirportReport = (): HokkaidoAirportReport => {
  return {
    id: 0,
    group1Id: 0,
    hokkaidoAirportCarId: 0,
    baseDepartureTs: null,
    operationStartTs: null,
    operationEndTs: null,
    baseArrivalTs: null,
    hanName: '',
    airportName: '',
    targetFacility: '',
    workAreas: '',
    workCycles: [],
    carType: '',
    standbyType: '',
    preWorkInspectionTs: null,
    preWorkInspectionResults: null,
    preWorkInspectionSkipped: false,
    postWorkInspectionTs: null,
    postWorkInspectionResults: null,
    postWorkInspectionSkipped: false,
  };
};

export const initCommonState = (): HokkaidoAirportState => {
  return {
    baseDepartureTime: null,
    workStartTime: null,
    workEndTime: null,
    currentTime: null,
    carUpdateTimer: null,
    workElapsedTimeNotifyTimer: null,
    mtxStoreTimer: null,
    mtxCreateTimer: null,
    status: 'stopped',
    hokkaidoAirportCar: {
      id: 0,
      currentReportId: null,
      report: initHokkaidoAirportReport(),
    },
    hokkaidoAirportMtxs: [],
    lastHokkaidoAirportMtxTs: null,
    errorModalMsg: '',
    isRequesting: false,
    showErrorModal: false,
    showGeolocationGeneralErrorMsg: false,
    showGeolocationPermissionErrorModal: false,
    externalReportSpLabels: {
      title: {},
      button: {},
      status: {},
      field_label: {},
    } as HokkaidoAirportLabelSp,
    currentScreenOverride: null,
  };
};

export const notifyWorkElapsedTimeSpeech = (workElapsedTimeInt: number, doSpeech: ((text: string) => void)): void => {
  // 15分毎に経過時間を通知する
  const thres = 900; // seconds
  const workElapsedTimeSec = timeIntegerToSeconds(workElapsedTimeInt);
  if (workElapsedTimeSec === 0) { return; }
  if (workElapsedTimeSec % thres > 0) { return; }

  const [h, m, s] = unpackTimeInteger(workElapsedTimeInt);
  let text = '作業開始から';
  if (h > 0) {
    text += `${h}時間`;
  }
  if (m > 0) {
    text += `${m}分`;
  }
  if (s > 0) {
    text += `${s}秒`;
  }
  text += 'が経過しました。';
  doSpeech(text);
};

// 報告書の点検・作業の記録から表示する画面を、上から順に判定する。
// 点検は完了なら点検時刻、スキップなら skipped が入る（開始前・終了時とも）
export const getHokkaidoAirportScreenName = (
  report: HokkaidoAirportScreenSourceReport,
): HokkaidoAirportScreenName => {
  if (!report.preWorkInspectionTs && !report.preWorkInspectionSkipped) return 'DepartureScreen';
  if (!report.operationStartTs) return 'StandbyScreen';
  if (!report.operationEndTs) return 'WorkingScreen';
  if (!report.postWorkInspectionTs && !report.postWorkInspectionSkipped) return 'PostWorkInspectionScreen';
  if (!report.baseArrivalTs) return 'PostWorkScreen';

  return 'DepartureScreen';
};

// ログイン中のユーザーが保存した内容だけを返す。別のユーザーやユーザーIDを持たない古い保存内容は null
export const findOwnHokkaidoAirportStorage = (
  storage: HokkaidoAirportReportLocalStorage | null,
  userId: number,
): HokkaidoAirportReportLocalStorage | null => {
  if (storage?.userId !== userId) return null;

  return storage;
};

export const isStaleDepartureStorage = (storage: HokkaidoAirportReportLocalStorage, now: Date): boolean => {
  if (!storage.baseDepartureTs) return false;
  // 車両の未登録時は hokkaidoAirportCar が無いことがあるため、報告書が無ければ開始前点検の前として扱う
  const report = storage.hokkaidoAirportCar?.report;
  if (report && getHokkaidoAirportScreenName(report) !== 'DepartureScreen') return false;

  const elapsedMsec = now.getTime() - new Date(storage.baseDepartureTs).getTime();
  return elapsedMsec > DEPARTURE_STORAGE_EXPIRE_HOURS * 60 * 60 * 1000;
};

export const resolveInitialStandbyType = (lastStandbyType: string, standbyTypes: string[]): string => {
  return standbyTypes.includes(lastStandbyType) ? lastStandbyType : '';
};

// 前回値が選択肢に無ければ未選択にする。車種は前回の空港の車種一覧にあるときだけ引き継ぐ
export const resolveInitialVehicleSelection = (
  lastSelection: HokkaidoAirportVehicleSelection | null,
  airports: string[],
  carTypesByAirport: Record<string, string[]>,
): HokkaidoAirportVehicleSelection => {
  if (!lastSelection || !airports.includes(lastSelection.airportName)) {
    return { airportName: '', carType: '' };
  }

  const carTypes = carTypesByAirport[lastSelection.airportName] ?? [];
  return {
    airportName: lastSelection.airportName,
    carType: carTypes.includes(lastSelection.carType) ? lastSelection.carType : '',
  };
};
